/**
 * Detects non-actionable feature-flag SDK fetch failures reported by the
 * Datadog OpenFeature provider.
 *
 * During initialization the `@datadog/openfeature-browser` SDK fetches flag
 * assignments from `preview.ff-cdn.*.datadoghq.com`. When the CDN is
 * unreachable (e.g. clients behind the Great Firewall of China, or bots
 * hitting the app via a bare IP / proxy domain), the fetch fails with a
 * `TypeError: Failed to fetch` (HTTP status 0). Datadog surfaces this in
 * two ways:
 *
 *  1. A **console-origin** error:
 *     message = "Error during provider initialization: TypeError: Failed to fetch"
 *     stack includes `fetchFlagsConfiguration` / `retrieveFlagsConfiguration`
 *
 *  2. A **network-origin** error:
 *     message = "Fetch error POST https://preview.ff-cdn.us5.datadoghq.com/..."
 *
 * Neither is actionable from application code. This filter drops both
 * variants from RUM Error Tracking and Browser Logs.
 */
export function isFeatureFlagFetchNoise(
  message: string | undefined,
  stack: string | undefined,
): boolean {
  if (!message) {
    return false;
  }

  const normalizedMessage = message.toLowerCase();

  if (_isProviderInitError(normalizedMessage)) {
    return true;
  }

  if (_isFeatureFlagCdnFetchError(normalizedMessage)) {
    return true;
  }

  if (_hasFetchErrorWithFlagStack(normalizedMessage, stack)) {
    return true;
  }

  return false;
}

function _isProviderInitError(normalizedMessage: string): boolean {
  return (
    normalizedMessage.includes("provider initialization") &&
    normalizedMessage.includes("failed to fetch")
  );
}

function _isFeatureFlagCdnFetchError(normalizedMessage: string): boolean {
  return (
    normalizedMessage.includes("ff-cdn") &&
    normalizedMessage.includes("datadoghq.com")
  );
}

function _hasFetchErrorWithFlagStack(
  normalizedMessage: string,
  stack: string | undefined,
): boolean {
  if (!normalizedMessage.includes("failed to fetch")) {
    return false;
  }
  if (!stack) {
    return false;
  }
  return (
    stack.includes("fetchFlagsConfiguration") ||
    stack.includes("retrieveFlagsConfiguration")
  );
}
